// gitlab-pipeline-ci-status.ts
//
// Which MR pipeline speaks for CI, and the `ciStatus` it yields (split out of
// review-remote-adapter.ts, 200-line budget).
//
// Choice (round 2 M1, independent review 2026-10-01): the pipeline OF THE PUSHED
// COMMIT with the highest id — never `pipelines[0]`. The order of
// `GET /merge_requests/:iid/pipelines` is not a contract (it merges
// merge-request-event and branch pipelines), and the verdict used to ride on it.
// Measured on git.nal.vn 2026-10-01: 5 MRs with >= 2 pipelines were all strictly
// id-descending and all `merge_request_event` — today's order happens to hold;
// the code does not depend on it. Pipeline ids only grow, so "highest id" is
// "latest run" (a retry re-runs the same id; "run pipeline" makes a higher one).
// The MR's `head_pipeline` (single-MR endpoint) matched in the 2 MRs sampled but
// can lag or be null right after a push, and is a different object from the list
// the X2 SHA bind reads — not used. With no pipeline of the pushed commit the
// highest id overall is reported, so X2 can name whose pipeline it is.
//
// `ciStatus` is that pipeline's OWN status — GitLab's computed verdict, which
// already accounts for `allow_failure` jobs and unfinished pipelines
// (release-ci-gate.ts turns it into a decision). The jobs are only a fallback for
// a pipeline that reports no status at all; they read the same vocabulary and,
// like GitLab, do not count a failed `allow_failure` job.
import type { GitlabJob, GitlabPipeline } from "./gitlab-merge-request-evidence-client.js";

function ciStatusFromJobs(jobs: GitlabJob[]): string {
  if (jobs.length === 0) return "unknown";
  const counted = jobs.filter((j) => !(j.allow_failure === true && j.status === "failed"));
  if (counted.every((j) => j.status === "success")) return "success";
  return counted.some((j) => j.status === "failed") ? "failed" : "running";
}

const highestId = (pipelines: GitlabPipeline[]): GitlabPipeline | undefined =>
  pipelines.reduce<GitlabPipeline | undefined>(
    (best, p) => (best === undefined || p.id > best.id ? p : best),
    undefined,
  );

/** `pushedSha` absent ⇒ the highest id overall. */
export function selectMergeRequestPipeline(
  pipelines: GitlabPipeline[],
  pushedSha?: string,
): GitlabPipeline | undefined {
  const ofPushedCommit =
    pushedSha === undefined ? [] : pipelines.filter((p) => p.sha === pushedSha);
  return highestId(ofPushedCommit.length > 0 ? ofPushedCommit : pipelines);
}

/** `pipeline` is the one chosen by selectMergeRequestPipeline; `jobs` are its jobs. */
export function gitlabCiStatus(
  pipeline: GitlabPipeline | undefined,
  jobs: GitlabJob[],
): { ciStatus: string; pipelineUrl?: string } {
  const status = pipeline?.status.trim() ?? "";
  return {
    ciStatus: status === "" ? ciStatusFromJobs(jobs) : status,
    ...(pipeline === undefined ? {} : { pipelineUrl: pipeline.web_url }),
  };
}
